// src/components/shared/InstallPrompt.js
import React, { useState, useEffect } from 'react';

const InstallPrompt = () => {
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isIOSChrome, setIsIOSChrome] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Don't show if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    // Don't show if already dismissed
    if (localStorage.getItem('gather-install-dismissed')) return;

    const ua = navigator.userAgent;
    const ios = /iphone|ipad|ipod/i.test(ua);
    // iOS Chrome identifies itself as "CriOS" in the user agent
    const iosChrome = ios && /crios/i.test(ua);

    setIsIOS(ios);
    setIsIOSChrome(iosChrome);

    if (ios) {
      // Show iOS instructions (Safari steps, or Chrome redirect) after 3 seconds
      const timer = setTimeout(() => setShow(true), 3000);
      return () => clearTimeout(timer);
    } else {
      // Android — listen for native install prompt
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShow(true);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const dismiss = () => {
    setShow(false);
    localStorage.setItem('gather-install-dismissed', 'true');
  };

  const install = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setShow(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.origin);
  };

  if (!show) return null;

  return (
    <div style={styles.toast}>
      <span style={styles.icon}>{isIOSChrome ? '🧭' : '📲'}</span>
      <div style={styles.body}>
        <div style={styles.title}>
          {isIOSChrome ? 'Open in Safari to install' : 'Add Gather to your home screen'}
        </div>
        <div style={styles.sub}>
          {isIOSChrome
            ? 'Chrome on iPhone can\'t install apps. Copy this link and open it in Safari instead.'
            : isIOS
              ? 'Tap the Share button below, then "Add to Home Screen"'
              : 'Install Gather as an app — works offline too'}
        </div>
        {isIOS && !isIOSChrome && (
          <div style={styles.iosArrow}>↓ Share button is at the bottom of your screen</div>
        )}
      </div>
      <div style={styles.actions}>
        {isIOSChrome && (
          <button style={styles.installBtn} onClick={copyLink}>Copy link</button>
        )}
        {!isIOS && !isIOSChrome && (
          <button style={styles.installBtn} onClick={install}>Install</button>
        )}
        <button style={styles.dismissBtn} aria-label="Dismiss" onClick={dismiss}>✕</button>
      </div>
    </div>
  );
};

const styles = {
  toast: {
    position: 'fixed',
    bottom: 96, left: 12, right: 12,
    maxWidth: 406,
    margin: '0 auto',
    background: 'var(--ink)',
    borderRadius: 18,
    padding: '14px 16px',
    display: 'flex', alignItems: 'flex-start', gap: 12,
    zIndex: 200,
    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
    animation: 'slideUp 0.4s ease forwards',
  },
  icon: { fontSize: 26, flexShrink: 0, marginTop: 2 },
  body: { flex: 1 },
  title: { fontSize: 13, fontWeight: 700, color: 'white', marginBottom: 3 },
  sub: { fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, fontWeight: 500 },
  iosArrow: {
    fontSize: 11, color: 'var(--sage-light)',
    fontWeight: 700, marginTop: 6,
  },
  actions: { display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 },
  installBtn: {
    background: 'var(--sage)',
    color: 'var(--sage-dark)',
    border: 'none',
    borderRadius: 10, padding: '8px 14px',
    fontSize: 12, fontWeight: 700,
    fontFamily: 'Nunito, sans-serif',
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  dismissBtn: {
    background: 'rgba(255,255,255,0.12)',
    color: 'rgba(255,255,255,0.7)',
    border: 'none', borderRadius: 8,
    width: 28, height: 28,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 700,
    fontFamily: 'Nunito, sans-serif', cursor: 'pointer',
  }
};

export default InstallPrompt;
